import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useCartActions } from '@webmono/storefront-ui/providers'
import type { QRContext } from '@webmono/storefront-ui/types'

interface QRCodeData {
  type: 'table' | 'promo' | 'secret_menu' | 'item' | 'category' | 'bundle'
  tableNumber?: number
  promoCode?: string
  promoPercent?: number
  promoCents?: number
  secretMenuCategoryId?: string
  itemId?: string
  categoryId?: string
  bundleItems?: string[]
  message: string
  redirectTo?: string
}

const QR_CODE_DATA: Record<string, QRCodeData> = {
  'table-1': { type: 'table', tableNumber: 1, message: 'Table 1 assigned' },
  'table-2': { type: 'table', tableNumber: 2, message: 'Table 2 assigned' },
  'table-3': { type: 'table', tableNumber: 3, message: 'Table 3 assigned' },
  'table-4': { type: 'table', tableNumber: 4, message: 'Table 4 assigned' },
  'table-5': { type: 'table', tableNumber: 5, message: 'Table 5 assigned' },
  'table-6': { type: 'table', tableNumber: 6, message: 'Table 6 assigned' },
  'table-7': { type: 'table', tableNumber: 7, message: 'Table 7 assigned' },
  'table-8': { type: 'table', tableNumber: 8, message: 'Table 8 assigned' },
  'summer10': { type: 'promo', promoCode: 'SUMMER10', promoPercent: 10, message: '10% off applied!' },
  'welcome15': { type: 'promo', promoCode: 'WELCOME15', promoPercent: 15, message: '15% off your first order!' },
  'free-delivery': { type: 'promo', promoCode: 'FREEDEL', promoCents: 599, message: 'Free delivery unlocked!' },
  'happy-hour': { type: 'promo', promoCode: 'HAPPYHOUR', promoPercent: 20, message: 'Happy Hour: 20% off!' },
  'secret': { type: 'secret_menu', secretMenuCategoryId: 'secret-menu', message: 'Secret menu unlocked! 🔓' },
  'try-california': { type: 'item', itemId: 'california-roll', message: 'Try our California Roll!', redirectTo: '/menu/california-roll' },
  'try-rainbow': { type: 'item', itemId: 'rainbow-roll', message: 'Try our Rainbow Roll!', redirectTo: '/menu/rainbow-roll' },
  'appetizers': { type: 'category', categoryId: 'appetizers', message: 'Appetizers menu', redirectTo: '/menu?category=appetizers' },
  'sushi-combo': { type: 'bundle', bundleItems: ['california-roll', 'miso-soup', 'edamame'], message: 'Sushi Combo selected! Add items to cart.' },
}

export function QRCodePage() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { setQRContext, applyPromoCode, setTableNumber } = useCartActions()
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [message, setMessage] = useState('')

  useEffect(() => {
    if (!code) {
      setStatus('error')
      setMessage('Invalid QR code')
      return
    }

    const qrData = QR_CODE_DATA[code.toLowerCase()]

    if (!qrData) {
      setStatus('error')
      setMessage('This QR code is not recognized')
      setTimeout(() => navigate('/menu'), 2000)
      return
    }

    const context: QRContext = {
      type: qrData.type,
      tableNumber: qrData.tableNumber,
      promoCode: qrData.promoCode,
      secretMenuCategoryId: qrData.secretMenuCategoryId,
      itemId: qrData.itemId,
      categoryId: qrData.categoryId,
      bundleItems: qrData.bundleItems,
      scannedAt: new Date().toISOString(),
    }

    setQRContext(context)

    if (qrData.type === 'table' && qrData.tableNumber) {
      setTableNumber(qrData.tableNumber)
    }

    if (qrData.type === 'promo' && qrData.promoCode) {
      applyPromoCode({
        code: qrData.promoCode,
        discountCents: qrData.promoCents || 0,
        discountPercent: qrData.promoPercent,
        description: qrData.message,
      })
    }

    setStatus('success')
    setMessage(qrData.message)

    const redirectTo = qrData.redirectTo || '/menu'
    setTimeout(() => navigate(redirectTo), 1500)
  }, [code, navigate, setQRContext, applyPromoCode, setTableNumber])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="text-center">
        {status === 'processing' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center animate-pulse">
              <svg className="w-8 h-8 text-muted-foreground animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </div>
            <p className="text-muted-foreground">Processing QR code...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-medium text-foreground">{message}</p>
            <p className="text-sm text-muted-foreground mt-2">Redirecting to menu...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-destructive/10 flex items-center justify-center">
              <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-lg font-medium text-foreground">{message}</p>
            <p className="text-sm text-muted-foreground mt-2">Redirecting to menu...</p>
          </>
        )}
      </div>
    </div>
  )
}
